import { useSyncExternalStore } from 'react';
import { readStorage, writeStorage } from '../Agent/storage';

// 输入条的两个形态（spec docs/design/input-center-collapse-mockup.html §03）：
// **收起态是默认的** —— 条不在屏幕底部，顶栏右上角那枚入口代表它；
// 展开态才是今天这根底部浮条，样子、面板、语法一个字没改。
//
// 结构照 Agent/launcherStore.ts，连同它为什么不住在组件里的理由：顶栏也要读
// 这个状态，而 InputCenter 是个 lazy chunk，顶栏不该为了一个布尔值把它拖进首屏。
const PINNED_KEY = 'input-center:pinned';

// expanded **不落盘**。「开着还是关着」是瞬时状态，不是偏好 —— 记住它的后果是：
// 你昨晚留着条没收，今早打开还是被占掉 88px，而你早就忘了那是自己上次留下的。
//
// pinned 才落盘：那是「我就要它一直在底下」。它同时是出了问题时**不发版就能
// 退回旧行为**的开关。
type State = { expanded: boolean; pinned: boolean };

// readStorage 读不到（隐私窗口、存储被禁）时返回 null，=== '1' 自然落在 false。
// 不要写成 != null —— 那会让读不到的人默认进「常驻」。
let state: State = { expanded: false, pinned: readStorage(PINNED_KEY) === '1' };

const listeners = new Set<() => void>();

function update(next: Partial<State>) {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useInputCenter(): State {
  return useSyncExternalStore(subscribe, () => state);
}

export function expandInputCenter() {
  if (!state.expanded) update({ expanded: true });
}

// 常驻开着时这里是空操作：开关写着「常驻底部」、条却被 Esc 收走了，是自相矛盾。
export function collapseInputCenter() {
  if (state.pinned) return;
  if (state.expanded) update({ expanded: false });
}

export function toggleInputCenterPinned() {
  const pinned = !state.pinned;
  writeStorage(PINNED_KEY, pinned ? '1' : null);
  // 打开常驻就是「现在就要它在底下」，所以顺手展开；关掉时维持现状，
  // 不要替人把条收走。
  update({ pinned, expanded: pinned || state.expanded });
}
